import type { CSSProperties, ReactNode } from 'react'
import React from 'react'
import classNames from 'classnames'

import type { Icon } from '@slite/icons'

import type { ButtonVariant } from '../Button/constants'
import { buttonVariantsClasses } from '../Button/constants'
import type { IconButtonIconSize, IconButtonSize } from './constants'
import { BASE_CLASSES, iconButtonSize, iconSizes } from './constants'

export type BaseIconButtonProps = {
  icon: Icon
  className?: string
  style?: CSSProperties
  variant?: ButtonVariant
  size?: IconButtonSize
  rounded?: boolean
  isActive?: boolean
  disabled?: boolean
  disableFocus?: boolean
  iconSize?: IconButtonIconSize
  iconClassname?: string
  iconStyle?: CSSProperties
  transparent?: boolean
  children: (
    outerClassName: string,
    outerStyle: CSSProperties,
    innerClassName: string,
    innerStyle: CSSProperties,
    children: ReactNode
  ) => ReactNode
}

// Variants that need a solid background layer
const VARIANTS_WITH_SOLID_BACKGROUND: ButtonVariant[] = [
  'primary',
  'secondary',
  'secondary-blue',
  'secondary-purple',
  'destructive',
]

const BORDER_RADIUS = {
  normal: {
    smaller: 6,
    small: 8,
    normal: 8,
    medium: 8,
    large: 8,
  },
  full: (size: IconButtonSize) => iconButtonSize[size] / 2,
} as const

export const BaseIconButton = ({
  className,
  style,
  variant = 'primary',
  size = 'medium',
  rounded = false,
  isActive = false,
  disabled = false,
  disableFocus,
  icon: IconComponent,
  iconClassname,
  iconSize = 'large',
  iconStyle,
  transparent = false,
  children: renderChildren,
}: BaseIconButtonProps) => {
  const needsSolidBackground =
    !transparent && VARIANTS_WITH_SOLID_BACKGROUND.includes(variant)

  const borderRadiusPx = rounded
    ? BORDER_RADIUS.full(size)
    : BORDER_RADIUS.normal[size]
  const borderRadiusClass = rounded
    ? 'radius-round'
    : size === 'smaller'
      ? 'radius-normal'
      : 'radius-large'

  // Outer element (button/link) gets solid background + border radius, no border
  const outerClassName = classNames(
    needsSolidBackground ? 'background-shade-100' : 'background-transparent',
    className
  )

  const outerStyle = {
    width: iconButtonSize[size],
    height: iconButtonSize[size],
    border: 'none',
    borderRadius: borderRadiusPx,
    ...style,
  }

  // Inner wrapper div gets all the icon button styling (including borders)
  const innerClassName = classNames(
    BASE_CLASSES,
    borderRadiusClass,
    [buttonVariantsClasses[variant].base],
    {
      'extra-white-outline-on-focus': !disableFocus && size !== 'smaller',
      // default
      [buttonVariantsClasses[variant].default]: !disabled && !isActive,

      // isActive
      [buttonVariantsClasses[variant].isActive]: isActive && !disabled,

      // disabled
      [buttonVariantsClasses[variant].disabled]:
        disabled && !isActive && !isActive,
    }
  )

  const innerStyle = {
    width: iconButtonSize[size],
    height: iconButtonSize[size],
    borderRadius: borderRadiusPx,
    cursor: disabled ? 'not-allowed' : 'pointer',
  }

  return (
    <>
      {renderChildren(
        outerClassName,
        outerStyle,
        innerClassName,
        innerStyle,
        <IconComponent
          className={iconClassname}
          size={iconSizes[iconSize]}
          style={iconStyle}
        />
      )}
    </>
  )
}
